import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  FlexVerticalAligned,
  Heading1,
} from "../../../common/components/styled/styled";
import { AnnouncementDialog } from "./AnnouncementDialog";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import { FormContainer } from "../../customer/customerManagement/styles";
import { Announcement } from "./Announcement";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { isFulfilled } from "@reduxjs/toolkit";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { AnnouncementItem, AnnouncementPayload } from "./types";
import {
  createAnnouncement,
  deleteAnnouncement,
  fetchAnnouncements,
  selectAll,
  updateAnnouncement,
} from "./announcementSlice";
import useIsLoading from "../../../hooks/useIsLoading";

export const AnnouncementsPanel = () => {
  const { t } = useTranslation("settings");
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(["announcements"]);
  const announcements = useAppSelector((state) => selectAll(state));

  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementItem | null>(null);

  const openAnnouncement = (id: string) => {
    const announcement = announcements.find((a) => a.id === id);
    if (!announcement) return;
    setSelectedAnnouncement(announcement);
    setIsAddDialogOpen(true);
  };

  const openNewAnnouncement = () => {
    setSelectedAnnouncement(null);
    setIsAddDialogOpen(true);
  };

  const toggleDialogOpen = (open: boolean) => {
    setIsAddDialogOpen(open);
    if (!open) setSelectedAnnouncement(null);
  };

  const onAddAnnouncement = (payload: AnnouncementPayload) => {
    dispatch(createAnnouncement(payload)).then((action) => {
      if (isFulfilled(action)) {
        toggleDialogOpen(false);
      }
    });
  };

  const onEditAnnouncement = (payload: AnnouncementItem) => {
    dispatch(updateAnnouncement(payload)).then((action) => {
      if (isFulfilled(action)) {
        toggleDialogOpen(false);
      }
    });
  };

  const onDeleteAnnouncement = (id: string, version: number) => {
    dispatch(
      deleteAnnouncement({
        id,
        version,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        toggleDialogOpen(false);
      }
    });
  };

  useEffect(() => {
    dispatch(fetchAnnouncements());
  }, [dispatch]);

  return (
    <FormContainer>
      <FlexVerticalAligned>
        <Heading1>{t("settings:announcements")}</Heading1>
        <AddButton
          label={t("settings:addNewAnnouncement")}
          onClick={openNewAnnouncement}
          variant="contained"
          sx={{ ml: 1 }}
        />
      </FlexVerticalAligned>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        announcements.map((a) => (
          <Announcement
            key={a.id}
            announcement={a}
            openAnnouncement={openAnnouncement}
            onDeleteAnnouncement={onDeleteAnnouncement}
          />
        ))
      )}
      <AnnouncementDialog
        open={isAddDialogOpen}
        toggleDialogOpen={toggleDialogOpen}
        announcement={selectedAnnouncement}
        handleAddAnnouncement={onAddAnnouncement}
        handleEditAnnouncement={onEditAnnouncement}
      />
    </FormContainer>
  );
};
